import React, {useCallback, useContext, useEffect} from 'react';
import { orderBy as _orderBy } from 'lodash';
import Pagination from '../../Components/Pagination';
import {IResultsData} from '../../Foundations/Types/IResultsData';
import SortArrowDown from '../../Components/Icons/SortArrowDown';
import SvgIcon from '../../Components/Icons/SvgIcon';
import SortArrowUp from '../../Components/Icons/SortArrowUp';
import resultsContext from '../../Foundations/Contexts/ResultsContext';
import {getSupplierName} from '../../Foundations/Utils/SupplierUtils';
import {getDepotAddress} from '../../Foundations/Utils/PDFUtils';

const MAX_RECORDS_ON_PAGE = 20;

const ResultsTableContainer: React.FC = () => {
    const { results, selectedItem, setSelectedItem, depotsAtArea } = useContext(resultsContext);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [sortedData, setSortedData] = React.useState<IResultsData[]>(results);
    const [sortedBy, setSortedBy] = React.useState();
    const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
    const [dataToDisplay, setDataToDisplay] = React.useState<IResultsData[]>(sortedData.slice(0, MAX_RECORDS_ON_PAGE));

    const getPageData = useCallback((fullList: IResultsData[]) => {
        const startIndex = (currentPage - 1) * MAX_RECORDS_ON_PAGE;

        return fullList.slice(startIndex, startIndex + MAX_RECORDS_ON_PAGE)
    }, [currentPage])

    const handleSort = useCallback((columnName): void => {
        const newOrder = sortedBy === columnName ? (order === 'asc' ? 'desc' : 'asc') : 'desc';

        setOrder(newOrder);
        setSortedBy(columnName);

        let sortBy = [columnName];
        let orderToSort = [newOrder]

        if (columnName === 'duration') {
            sortBy = [columnName, 'total_rate_estimate']
            orderToSort = [newOrder, newOrder]
        }

        if (columnName === 'distance') {
            sortBy = ['depot_code', columnName]
            orderToSort = [newOrder, newOrder]
        }

        const sortedData = _orderBy(
            results,
            sortBy,
            orderToSort
        );
        setSortedData(sortedData);

        setDataToDisplay(getPageData(sortedData))
    }, [sortedBy, results, setSortedData, order, getPageData]);

    useEffect(() => {
        setSortedData(results);
        setDataToDisplay(results.slice(0, MAX_RECORDS_ON_PAGE));
        setCurrentPage(1); // reset to first page when results changed
    }, [results]);

    const handleChangePage = (page): void => {
        let newStartIndex: number = (page < currentPage ? page - 1 : page) * MAX_RECORDS_ON_PAGE;
        let endIndex: number = newStartIndex + MAX_RECORDS_ON_PAGE;

        if (newStartIndex > sortedData.length) {
            const remainder: number = sortedData.length % MAX_RECORDS_ON_PAGE;
            newStartIndex = sortedData.length - remainder;
        }

        setDataToDisplay(sortedData.slice(newStartIndex, endIndex))
        setCurrentPage(page);
    };

    const renderTableHeader = () => {
        const renderHeaderCell = (columnName: string, title: string, subtitle?: string, justifyCenter?: boolean) => (
            <div className={`flex align-center ${justifyCenter? `justify-center` : ''}`}>
                <div className="font-bold flex flex-col justify-center">
                    {title}
                    <br/>
                    <span className="font-normal">{subtitle}</span>
                </div>
                <span onClick={(): void => handleSort(columnName)}>
                    <SvgIcon className="ml-2 fill-gray-500 cursor-pointer">
                        {order === 'desc' && sortedBy === columnName
                            ? <SortArrowUp />
                            : <SortArrowDown />
                        }
                    </SvgIcon>
                </span>
            </div>
        )

        return (
            <div className="h-14 grid grid-cols-6 grid-rows-1 items-center bg-gray-400 px-6 rounded-t-lg">
                {renderHeaderCell('per_day_price', 'Per day')}
                {renderHeaderCell('duration', 'Duration', 'Total $')}
                {renderHeaderCell('vehicle_data.name', 'Model', 'Class - Type - SIPP')}
                {renderHeaderCell('vehicle_data.seats', 'Seats', undefined, true)}
                {renderHeaderCell('supplier_code', 'Supplier')}
                {renderHeaderCell('distance', 'Depot location')}
            </div>
        );
    }

    const renderTableBody = () => (
        <>
            <div className={`grid grid-cols-1 grid-rows-${dataToDisplay.length}`}>
                {dataToDisplay.map((item, index) => renderTableRow(item, index))}
            </div>
            <Pagination
                currentPage={currentPage}
                onChangePage={handleChangePage}
                pageCount={Math.ceil(sortedData.length / MAX_RECORDS_ON_PAGE)}
            />
        </>
    );

    const renderTableRow = (item: IResultsData, index: number) => {
        const renderRowCell = (value: string | number, subValue?: string | number, titleSuffix?: string, centerText?: boolean) => (
            <div className="font-bold flex flex-col justify-center">
                <div className={centerText? 'text-center' : ''}>
                    {value} <span className="font-normal inline-block">{titleSuffix}</span>
                </div>
                <br/>
                <span className="font-normal">{subValue}</span>
            </div>
        )

        return (
            <div
                key={`${item.id}-${index}`}
                onClick={() => setSelectedItem(selectedItem !== null && selectedItem.id === item.id ? null : item)}
                className={`min-h-14 grid grid-cols-6 grid-rows-1 items-center border-b-4 border-gray-100 px-6 py-2 ${selectedItem !== null && selectedItem.id === item.id ? 'text-white bg-blue-600' : 'bg-white'}`}
            >
                {renderRowCell(`$${(typeof item.per_day_price !== 'undefined' ? item.per_day_price : item.total_rate_estimate/item.duration).toFixed(2)}`)}
                {renderRowCell(`${item.duration}d`, `$${item.total_rate_estimate.toFixed(2)}`)}
                {renderRowCell(item.vehicle_data.name, `${item.vehicle_data.category} - ${item.vehicle_data.type} - ${item.sipp_code}`)}
                {renderRowCell(item.vehicle_data.seats, undefined, undefined, true)}
                {renderRowCell(item.supplier_name || getSupplierName(item.supplier_code))}
                {
                    renderRowCell(
                        item.depot_code,
                        getDepotAddress(depotsAtArea, `${item.depot_code}_${item.supplier_code}`),
                        typeof item.distance !== 'undefined' ? `(${item.distance.toFixed(2)}Km from location)` : ''
                    )
                }
            </div>
        )
    }

    return (
        <div className="text-xs pb-12 text-gray-700">
            <div>{renderTableHeader()}</div>
            <div>{renderTableBody()}</div>
        </div>
    )
}

export default ResultsTableContainer
